import { CandleType, DomPosition, type Chart, type OverlayCreate, type OverlayMode } from 'klinecharts';
import { useChartStore } from '@/store/chartStore';
import { useAuthStore } from '@/store/authStore';
import { ensureBreadthData } from '@/store/breadthStore';
import { INDICATORS, INDICATOR_DEFAULT_COLORS } from './indicatorDefs';
import { HLC_UP_COLOR, HLC_DOWN_COLOR, HLC_NO_CHANGE_COLOR } from '@/indicators/hlcBars';
import { DEFAULT_CUP_HANDLE_SETTINGS } from '@/indicators/cupHandle';
import { DEFAULT_WILLIAM_ONEIL_SELL_SETTINGS } from '@/indicators/williamOneilSell';
import { hexToRgba } from '@/utils/color';
import { createRsiCompanions } from './rsiCompanions';
import type { IndicatorTemplateData, SerializedOverlay } from '@/types';

type ChartV9 = Chart & {
  createIndicator: (name: string, isStack: boolean, paneOptions?: { id?: string; height?: number; minHeight?: number }) => string;
  removeIndicator: (paneId: string, name?: string) => void;
  overrideIndicator: (override: { name: string; calcParams?: number[]; styles?: Record<string, unknown> }, paneId?: string) => void;
  getSize: (paneId: string, position: DomPosition) => { width: number; height: number; top: number } | null;
};

const DEFAULT_VOL_MA = [5, 10, 20];

export type ApplicableTemplate = Omit<IndicatorTemplateData, 'version'>;

function restoreDrawings(
  chart: ChartV9,
  drawings: SerializedOverlay[],
  paneMap: Map<string, string>,
  symbol: string,
): void {
  const { addDrawingOverlay } = useChartStore.getState();
  for (const d of drawings) {
    // Sub-pane ids differ per session — resolve via the owning indicator's new pane.
    const paneId = d.paneIndicator ? paneMap.get(d.paneIndicator) : d.paneId;
    if (!paneId) continue; // indicator wasn't restored (e.g. Pro-only for this viewer)
    // createOverlay silently falls back to the candle pane for an unknown pane,
    // which would put a sub-pane drawing at the wrong price scale — skip instead.
    if (paneId !== 'candle_pane' && !chart.getSize(paneId, DomPosition.Root)) continue;
    try {
      const create: OverlayCreate = {
        name: d.name,
        points: d.points,
        mode: d.mode as OverlayMode | undefined,
        lock: d.lock,
        visible: d.visible,
        styles: d.styles as OverlayCreate['styles'],
        extendData: d.extendData,
      };
      const id = chart.createOverlay(create, paneId);
      if (typeof id === 'string') addDrawingOverlay(id, symbol);
    } catch { /* overlay type not registered / pane gone — skip this drawing */ }
  }
}

/**
 * Replaces the chart's indicators + settings with a template's: everything a
 * saved template or a shared link can carry. Drawings are restored last, once
 * their target panes exist.
 */
export function applyTemplateToChart(
  chart: Chart,
  template: ApplicableTemplate,
  // allowAdvanced: skip the viewer's plan check. Only for shared links, whose
  // Pro features were already gated against the *creator's* plan server-side.
  opts: { symbol?: string; allowAdvanced?: boolean } = {},
): void {
  const c = chart as ChartV9;
  const store = useChartStore.getState();
  const user = useAuthStore.getState().user;
  const candleType = template.chartSettings?.candleType;

  // Clear all indicator params so stale values from previous indicators
  // don't cause false dirty state or hide real changes.
  useChartStore.setState({ indicatorParams: {} });
  // Clear scan configs so stale rules from previous template don't linger
  store.clearAllIndicatorScanConfigs();
  // Restore RS lines to match the template (or clear)
  store.setRSLineConfigs(template.rsLineConfigs ?? []);
  // Restore volume label toggle (default off when not stored)
  store.setVolumeLabelEnabled(template.volumeLabelEnabled ?? false);
  // Restore EPS Line (secondary axis) settings
  store.setEpsLineEnabled(template.epsLineEnabled ?? false);
  store.setEpsLineAlignToAnnouncement(template.epsLineAlignToAnnouncement ?? true);
  store.setEpsLineAxisScale(template.epsLineAxisScale ?? 'log');
  store.setEpsLineForwardModel(template.epsLineForwardModel ?? 'ttm-yoy');
  store.setEpsLineShowForwardProjection(template.epsLineShowForwardProjection ?? false);
  // Restore Foreign Holding sub-pane settings (all toggles default true)
  store.setForeignHoldingUpColor(template.foreignHoldingUpColor ?? '#22C55E');
  store.setForeignHoldingDownColor(template.foreignHoldingDownColor ?? '#EF4444');
  store.setForeignHoldingFlatColor(template.foreignHoldingFlatColor ?? '#9CA3AF');
  store.setForeignHoldingMA1Period(template.foreignHoldingMA1Period ?? 10);
  store.setForeignHoldingMA1Color(template.foreignHoldingMA1Color ?? '#3B82F6');
  store.setForeignHoldingMA2Period(template.foreignHoldingMA2Period ?? 20);
  store.setForeignHoldingMA2Color(template.foreignHoldingMA2Color ?? '#EC4899');
  store.setForeignHoldingConfirmBullColor(template.foreignHoldingConfirmBullColor ?? '#22C55E');
  store.setForeignHoldingDistributionColor(template.foreignHoldingDistributionColor ?? '#F59E0B');
  store.setForeignHoldingAccumulationColor(template.foreignHoldingAccumulationColor ?? '#8B5CF6');
  store.setForeignHoldingConfirmBearColor(template.foreignHoldingConfirmBearColor ?? '#EF4444');
  // Restore Cup & Handle pattern overlay settings
  store.setCupHandleEnabled(template.cupHandleEnabled ?? false);
  store.setCupHandleSettings(template.cupHandleSettings ?? DEFAULT_CUP_HANDLE_SETTINGS);
  // Restore William O'Neil sell signal overlay settings
  store.setWilliamOneilSellEnabled(template.williamOneilSellEnabled ?? false);
  store.setWilliamOneilSellSettings(template.williamOneilSellSettings ?? DEFAULT_WILLIAM_ONEIL_SELL_SETTINGS);

  // Restore chart visual settings
  const cs = template.chartSettings ?? {};
  const up = cs.candleUpColor ?? '#26a69a';
  const down = cs.candleDownColor ?? '#ef5350';
  useChartStore.setState({
    candleUpColor: up,
    candleDownColor: down,
    hlcUpColor: cs.hlcUpColor ?? '#22C55E',
    hlcDownColor: cs.hlcDownColor ?? '#EF4444',
    showHGrid: cs.showHGrid ?? true,
    showVGrid: cs.showVGrid ?? true,
    showPriceMarkHigh: cs.showPriceMarkHigh ?? true,
    showPriceMarkLow: cs.showPriceMarkLow ?? true,
    showLastPriceLine: cs.showLastPriceLine ?? true,
    showCrosshair: cs.showCrosshair ?? true,
    yAxisReverse: cs.yAxisReverse ?? false,
    priceScaleType: (cs.priceScaleType as 'log' | 'normal' | 'percentage' | undefined) ?? 'normal',
  });
  // Apply to chart immediately
  c.setStyles({
    candle: {
      bar: { upColor: up, upBorderColor: up, upWickColor: up, downColor: down, downBorderColor: down, downWickColor: down },
      priceMark: {
        show: (cs.showPriceMarkHigh ?? true) || (cs.showPriceMarkLow ?? true) || (cs.showLastPriceLine ?? true),
        high: { show: cs.showPriceMarkHigh ?? true },
        low:  { show: cs.showPriceMarkLow ?? true },
        last: { show: cs.showLastPriceLine ?? true, line: { show: cs.showLastPriceLine ?? true } },
      },
    },
    grid: {
      horizontal: { show: cs.showHGrid ?? true },
      vertical:   { show: cs.showVGrid ?? true },
    },
    crosshair: { show: cs.showCrosshair ?? true },
    yAxis: { reverse: cs.yAxisReverse ?? false } as never,
  });

  // Always clean up HLC (it's not tracked in paneMap)
  try { c.removeIndicator('candle_pane', 'HLC'); } catch { /* ignore */ }

  // Step 1: Remove all non-VOL indicators — chart returns to "fresh" state.
  store.indicatorPaneMap.forEach((paneId, indName) => {
    if (indName !== 'VOL') c.removeIndicator(paneId, indName);
  });
  const accumulated = new Map<string, string>([['VOL', 'vol_pane']]);
  store.setIndicatorPaneMap(new Map(accumulated));

  // Apply saved candle type
  if (candleType) {
    if (candleType === 'hlc') {
      c.setStyles({ candle: { type: CandleType.Area, area: { lineColor: 'rgba(0,0,0,0)', backgroundColor: 'rgba(0,0,0,0)' } } });
      setTimeout(() => {
        const live = useChartStore.getState().chart as ChartV9 | null;
        live?.createIndicator('HLC', true, { id: 'candle_pane' });
        live?.setStyles({ indicator: { ohlc: { upColor: HLC_UP_COLOR, downColor: HLC_DOWN_COLOR, noChangeColor: HLC_NO_CHANGE_COLOR } } });
      }, 0);
    } else if (candleType === CandleType.Area) {
      c.setStyles({
        candle: { type: CandleType.Area, area: { lineColor: up, backgroundColor: hexToRgba(up, 0.15) } },
      });
    } else {
      c.setStyles({ candle: { type: candleType as CandleType } });
    }
  }

  // Apply saved VOL params/colors, or defaults if missing.
  const volEntry = template.indicators.find(({ name: n }) => n === 'VOL');
  const volParams = volEntry?.params?.length ? volEntry.params : DEFAULT_VOL_MA;
  const barDefaults = INDICATOR_DEFAULT_COLORS.VOL ?? ['#26A69A', '#EF5350'];
  const maDefaults = INDICATOR_DEFAULT_COLORS.MA ?? ['#FF9600', '#9D65C9', '#2196F3', '#E11D74'];
  const barColors = [
    volEntry?.colors?.[0] ?? barDefaults[0],
    volEntry?.colors?.[1] ?? barDefaults[1] ?? barDefaults[0],
  ];
  const maColors = volParams.map((_, i) => volEntry?.colors?.[2 + i] ?? maDefaults[i % maDefaults.length]);
  const volColors = [...barColors, ...maColors];
  c.overrideIndicator(
    {
      name: 'VOL',
      calcParams: volParams,
      styles: {
        bars: [{ style: 'fill', upColor: barColors[0], downColor: barColors[1], noChangeColor: barColors[0] }],
        lines: maColors.map((color) => ({ style: 'solid', smooth: false, size: 1, dashedValue: [2, 2], color })),
      },
    } as never,
    'vol_pane',
  );
  store.setIndicatorColors('VOL', volColors);
  store.setIndicatorParam('VOL', volParams);

  // Step 2: Add each indicator one frame at a time, exactly as if the user
  // were clicking each button individually.  This lets KlineCharts run its
  // internal height-redistribution logic between each createIndicator call.
  // Advanced indicators are Pro/Business only — a saved template can't be
  // used to smuggle them past a basic-plan account.
  const hasAdvancedAccess = opts.allowAdvanced || user?.plan === 'pro' || user?.plan === 'business';
  const toAdd = template.indicators.filter(({ name: tName }) => {
    if (tName === 'VOL') return false;
    const def = INDICATORS.find((i) => i.name === tName);
    if (def?.category === 'advanced' && !hasAdvancedAccess) return false;
    return true;
  });

  // Ensure breadth data is available before any createIndicator call.
  // With localStorage persistence this resolves instantly (cache hit).
  const hasAdvanced = toAdd.some(
    ({ name: tName }) => INDICATORS.find((i) => i.name === tName)?.category === 'advanced'
  );
  const breadthReady = hasAdvanced ? ensureBreadthData() : Promise.resolve(true);

  void breadthReady.then(() => {
    // Add all indicators synchronously in one pass — mirrors the Ctrl+R restore path
    // in ChartContainer which works correctly.  The previous staggered setTimeout
    // (idx * 20 ms) caused klinecharts to inflate its internal pane-height total in
    // separate frames so rebalancePanes computed wrong proportions each time and
    // indicators appeared one-by-one with a visible delay.
    const live = useChartStore.getState().chart as ChartV9 | null;
    const subPaneIds = new Set<string>();
    if (live) {
      const s = useChartStore.getState();
      for (const { name: tName, params, colors } of toAdd) {
        if (tName === 'RS_LINE') continue;
        const def = INDICATORS.find((i) => i.name === tName);
        if (!def) continue;
        const opts2 = def.paneId
          ? { id: def.paneId }
          : { height: def.paneHeight ?? 100, minHeight: 60 };
        const returnedPaneId = live.createIndicator(tName, !!def.paneId, opts2);
        const resolvedId = def.paneId ?? returnedPaneId ?? tName;
        accumulated.set(tName, resolvedId);
        if (tName === 'RSI') createRsiCompanions(live, resolvedId);
        if (!def.paneId) subPaneIds.add(resolvedId);
        if (params?.length) {
          live.overrideIndicator({ name: tName, calcParams: params }, resolvedId);
          s.setIndicatorParam(tName, params);
        }
        if (colors?.length) {
          // VOL_SPIKE_* indicators render entirely via draw() with size:0 hidden lines.
          // Overriding styles with size:1 would make klinecharts try to render the raw
          // volume/pct lines and distort the Y axis. Only persist the label colour.
          if (!tName.startsWith('VOL_SPIKE')) {
            live.overrideIndicator({ name: tName, styles: { lines: colors.map((col) => ({ style: 'solid', smooth: false, size: 1, dashedValue: [2, 2], color: col })) } } as never, resolvedId);
          }
          s.setIndicatorColors(tName, colors);
        }
      }
      // RS lines are drawn by an overlay indicator on the candle pane. useRSLines
      // only creates it while RS_LINE is *missing* from the pane map, so it must
      // be created here before RS_LINE is recorded — otherwise the RS data loads
      // but nothing ever draws it.
      if (template.rsLineConfigs?.some((rs) => rs.visible)) {
        try { live.createIndicator('RS_LINE', true, { id: 'candle_pane' }); } catch { /* already on the chart */ }
        accumulated.set('RS_LINE', 'candle_pane');
      }
      s.setIndicatorPaneMap(new Map(accumulated));
    }

    const s = useChartStore.getState();
    if (template.scanConfigs) {
      Object.entries(template.scanConfigs).forEach(([indName, configs]) =>
        s.setIndicatorScanConfigs(indName, configs)
      );
    }
    if (template.rsLineConfigs) s.setRSLineConfigs(template.rsLineConfigs);

    // Drawings go last: sub-pane drawings need their indicator's pane to exist.
    if (live && template.drawings?.length) {
      restoreDrawings(live, template.drawings, accumulated, opts.symbol ?? s.symbol);
    }

    const runRebalance = () => {
      void useChartStore.getState().rebalancePanes();
      setTimeout(() => { void useChartStore.getState().rebalancePanes(); }, 300);
    };

    const waitForPanesReady = (cc: ChartV9, paneIds: string[], frame = 0) => {
      if (paneIds.length === 0) { runRebalance(); return; }
      const ready = paneIds.every((paneId) => {
        const size = cc.getSize(paneId, DomPosition.Root);
        return !!size && size.height > 0;
      });
      if (ready || frame >= 20) { runRebalance(); return; }
      requestAnimationFrame(() => waitForPanesReady(cc, paneIds, frame + 1));
    };

    if (live) {
      waitForPanesReady(live, [...subPaneIds]);
    } else {
      runRebalance();
    }
  });
}
